import React, { useEffect, useState } from "react";
import * as api from "../api";
import AudioPlayer from "./AudioPlayer";

const BOX =
  "rounded-xl border border-[#1f2638] bg-[#0e1320]/40 p-3 text-sm text-slate-400";

function safePart(s) {
  const v = String(s || "").trim();
  if (!v) return "";

  return v
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\-_]+/gu, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function getExt(origName) {
  const base = String(origName || "").trim();
  const m = base.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : "mp3";
}

function buildConvaiAudioName(origName, meta = {}) {
  const ext = getExt(origName);
  const id = safePart(meta?.conversationId) || "unknown";
  const nm = safePart(meta?.clientName);

  const parts = ["out"];
  if (nm) parts.push(nm);
  parts.push(id);

  return `${parts.join("_")}.${ext}`;
}

function isMissingAudioError(message) {
  const lowerMsg = String(message || "").trim().toLowerCase();
  return (
    lowerMsg.includes("audio not found") ||
    lowerMsg.includes("not found") ||
    lowerMsg.includes("(404)") ||
    lowerMsg.includes(" 404") ||
    lowerMsg.startsWith("404")
  );
}

export default function ConvaiAudioPlayer({
  conversationId,
  className = "",
  clientName = "",
  clientEgn = "",
}) {
  const [srcUrl, setSrcUrl] = useState("");
  const [downloadName, setDownloadName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  useEffect(() => {
    let alive = true;

    setSrcUrl("");
    setDownloadName("");
    setErr("");

    const callId = String(conversationId || "").trim();
    if (!callId) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);

        const meta = await api.getCallAudioSignedUrl(callId);
        const signedUrl = String(meta?.url || "").trim();
        if (!signedUrl) throw new Error("Няма аудио URL.");

        const fileName = buildConvaiAudioName(meta?.filename || `${callId}.mp3`, {
          conversationId: callId,
          clientName,
        });

        if (alive) {
          setSrcUrl(signedUrl);
          setDownloadName(fileName);
        }
      } catch (e) {
        const msg = String(e?.message || "").trim();
        if (!isMissingAudioError(msg)) {
          console.error(e);
        }

        if (alive) {
          setErr(isMissingAudioError(msg) ? "" : (msg || "Няма аудио или няма достъп."));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [conversationId, clientName, clientEgn]);

  const download = async () => {
    const callId = String(conversationId || "").trim();
    if (!callId || downloadBusy) return;

    try {
      setDownloadBusy(true);

      const { blob, filename } = await api.downloadCallAudio(callId);

      const fileName = buildConvaiAudioName(
        filename || downloadName || `${callId}.mp3`,
        {
          conversationId: callId,
          clientName,
        }
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Грешка при сваляне на аудио.");
    } finally {
      setDownloadBusy(false);
    }
  };

  if (!conversationId) return null;

  if (loading) return <div className={BOX}>Зареждане на аудио...</div>;
  if (err) return <div className={BOX}>{err}</div>;
  if (!srcUrl) return <div className={BOX}>Няма аудио файл.</div>;

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-end">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={download}
          disabled={downloadBusy}
          title="Свали аудио файла"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="mr-1"
          >
            <path d="M12 3v12" />
            <path d="M7 10l5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
          {downloadBusy ? "Сваляне..." : "Свали"}
        </button>
      </div>

      <AudioPlayer src={srcUrl} />
    </div>
  );
}
